/**
 * Resolves a Formly-style model path from a double-click inside ngx-json-viewer DOM.
 */
export function resolveNgxJsonViewerPath(target: HTMLElement, root?: HTMLElement): string | null
{
  const segmentMain = target.closest('.segment-main');
  if (!segmentMain)
  {
    return null;
  }

  const boundary = root ?? (target.closest('.ngx-json-viewer-path-root') as HTMLElement) ?? document.body;
  const parts: string[] = [];
  let segment = segmentMain.closest('.segment') as HTMLElement | null;

  while (segment && boundary.contains(segment))
  {
    const key = segment
      .querySelector(':scope > .segment-main > .segment-key')
      ?.textContent
      ?.trim();

    if (key && !key.startsWith('('))
    {
      parts.unshift(/^\d+$/.test(key) ? `[${key}]` : key);
    }

    segment = findParentSegment(segment, boundary);
  }

  return parts.length ? toFormlyPatientExpression(parts) : null;
}

/** Prefix used in Formly expressions to read the patient from formState. */
export const FORMLY_PATIENT_EXPRESSION_PREFIX = 'field?.options?.formState?.patient?.';

function findParentSegment(segment: HTMLElement, boundary: HTMLElement): HTMLElement | null
{
  let el: HTMLElement | null = segment.parentElement;

  while (el && boundary.contains(el))
  {
    if (el.classList.contains('children'))
    {
      const parentSegment = el.parentElement;
      return parentSegment?.classList.contains('segment') ? parentSegment : null;
    }
    el = el.parentElement;
  }

  return null;
}

/**
 * Builds a path using optional chaining for safe property access.
 * e.g. ['contact', 'homePhoneNumberExtension'] -> contact?.homePhoneNumberExtension
 */
export function toSafeAccessPath(parts: string[]): string
{
  return parts.reduce((acc, part) =>
  {
    if (part.startsWith('['))
    {
      return acc ? `${acc}?.${part}` : part;
    }
    return acc ? `${acc}?.${part}` : part;
  }, '');
}

/**
 * Wraps a patient model path in a Formly expression for default values, hide, etc.
 * e.g. contact + homePhoneNumberExtension ->
 *   ${field?.options?.formState?.patient?.contact?.homePhoneNumberExtension}
 */
export function toFormlyPatientExpression(parts: string[]): string
{
  const patientPath = toSafeAccessPath(parts);
  return `\${${FORMLY_PATIENT_EXPRESSION_PREFIX}${patientPath}}`;
}

export function clearNgxJsonViewerPathSelection(container: HTMLElement): void
{
  container.querySelectorAll('.segment-main.json-path-selected').forEach((el) =>
  {
    el.classList.remove('json-path-selected');
  });
}

export function highlightNgxJsonViewerPathSegment(target: HTMLElement): void
{
  target.closest('.segment-main')?.classList.add('json-path-selected');
}
